import { HOUSEHOLD_MEMBER_COLORS } from '../caremap/CareMapFactory';

const WHITE = '#FFFFFF';
const DARK = '#1A1A1A';

export const buildDisplayName = ({ firstName, lastName }: { firstName?: string; lastName?: string }): string => {
    return [firstName, lastName].filter(Boolean).join(' ');
};

/**
 * Two letters, from the first and last word of the name. A single-word name yields one letter,
 * and an empty name yields an empty string -- callers treat that as "no identity to show".
 */
export const getInitials = (name: string | undefined): string => {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) {
        return '';
    }
    const first = parts[0].charAt(0);
    const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
    return `${first}${last}`.toUpperCase();
};

/**
 * FNV-1a. Any stable arithmetic hash would do; what matters is that it is deterministic across
 * sessions and platforms, so a person's colour never changes underneath them.
 */
const hash = (value: string): number => {
    let result = 0x811c9dc5;
    for (let i = 0; i < value.length; i++) {
        result ^= value.charCodeAt(i);
        result = Math.imul(result, 0x01000193);
    }
    return Math.abs(result);
};

/**
 * Derived from the whole name rather than the initials, so people who share initials are spread
 * across the palette instead of all sharing one colour. Deriving from the name rather than a user
 * id keeps it available everywhere initials are rendered without threading extra props, and means
 * the colour stays coherent with the letters if someone's name changes.
 */
export const getAvatarColor = (name: string | undefined): string => {
    const normalized = (name || '').trim().toLowerCase().replace(/\s+/g, ' ');
    return HOUSEHOLD_MEMBER_COLORS[hash(normalized) % HOUSEHOLD_MEMBER_COLORS.length];
};

// The illustrated avatars carry their palette colour at roughly 30% over white rather than at
// full strength -- measured off the artwork, where e.g. 3dog.png's background wash is at alpha 77
// of 255. Initials and the placeholder figure use the same treatment so a circle of letters sits
// at the same visual weight as a circle holding an illustration.
const SURFACE_OPACITY = 0.3;

export const toAvatarSurfaceColor = (hex: string): string => {
    const value = hex.replace('#', '');
    if (value.length !== 6) {
        return hex;
    }
    const blend = (channel: number): number => Math.round(channel * SURFACE_OPACITY + 255 * (1 - SURFACE_OPACITY));
    const r = blend(parseInt(value.substring(0, 2), 16));
    const g = blend(parseInt(value.substring(2, 4), 16));
    const b = blend(parseInt(value.substring(4, 6), 16));
    return `#${[r, g, b].map(c => c.toString(16).padStart(2, '0').toUpperCase()).join('')}`;
};

const toLinear = (channel: number): number => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
};

const relativeLuminance = (hex: string): number => {
    const value = hex.replace('#', '');
    const r = parseInt(value.substring(0, 2), 16);
    const g = parseInt(value.substring(2, 4), 16);
    const b = parseInt(value.substring(4, 6), 16);
    return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
};

/**
 * Computed rather than held in a per-colour map, because parts of the palette are too light for
 * white text and a hand-maintained map silently rots when a colour is added or changed.
 */
export const getContrastingTextColor = (backgroundHex: string): string => {
    const luminance = relativeLuminance(backgroundHex);
    if (Number.isNaN(luminance)) {
        // a colour we cannot parse tells us nothing about contrast, and dark text is legible on
        // more of the palette than white is
        return DARK;
    }
    const contrastWithWhite = 1.05 / (luminance + 0.05);
    const contrastWithDark = (luminance + 0.05) / (relativeLuminance(DARK) + 0.05);
    return contrastWithWhite >= contrastWithDark ? WHITE : DARK;
};
